/*
 * File: TextInput.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 9, 2022 at 4:02 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 5:51 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import { Typography } from '../Typography';

// Styles
import * as S from './TextInput.styles';


/**
 * Component
 */

export const TextInput = forwardRef(({
	id, label, error, type, size, icon, containerClassName, characterLimit, defaultHideCharacterLimit, value, onChange, ...rest
}, ref) => {

	// Get character count
	const characterCount = useMemo(() => (value ? value.length : 0), [value]);

	// Set if should show character limit
	const showCharacterLimit = useMemo(() => {
		if (characterLimit > 0) {
			if (!defaultHideCharacterLimit) return true;
			return (characterCount / characterLimit) > 0.8;
		}
		return false;
	}, [characterCount, characterLimit, defaultHideCharacterLimit]);

	// Create on change function
	const innerOnChange = (e) => {

		// Get content
		let { value: content } = e.target;

		// Restrict if necessary
		if (characterLimit > 0 && content.length > characterLimit) {
			content = content.substring(0, characterLimit);
		}

		// Fire on change
		onChange({ ...e, target: { ...e.target, name: e.target.name, value: content } });
	};

	// Render component
	return (
		<S.Wrapper $error={error} className={containerClassName}>

			{/* Label */}
			{label && (
				<Typography tag="label" weight="semibold" htmlFor={id ? `${id}` : null} id={id ? `${id}-label` : null} className="animate">
					{label}
				</Typography>
			)}

			{/* Icon */}
			{icon && (
				<S.IconWrapper>
					<FontAwesomeIcon icon={icon} />
				</S.IconWrapper>
			)}

			{/* Character Limit */}
			{showCharacterLimit && (
				<S.CharacterLimit $limitReached={characterCount >= characterLimit}>
					<Typography variation="3">
						{characterCount}
						{' '}
						/
						{' '}
						{characterLimit}
					</Typography>
				</S.CharacterLimit>
			)}

			{/* Input */}
			<S.TextInput
				ref={ref}
				id={id}
				$error={error}
				{...rest}
				value={value}
				onChange={innerOnChange}
				type={type}
				$size={size}
				$withIcon={!!icon}
				className={rest.className ? `${rest.className} animate` : 'animate'}
			/>

			{/* Error */}
			{error && error.message && (
				<S.FloatingWrapper title={error.message} className="animate">
					<Typography tag="p" variation="3" className="animate">
						{error.message}
					</Typography>
				</S.FloatingWrapper>
			)}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

TextInput.displayName = 'TextInput';
TextInput.propTypes = {
	icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	type: PropTypes.oneOf(['text', 'password', 'email', 'search', 'number']),
	size: PropTypes.oneOf(['fit', 'small', 'medium']),
	id: PropTypes.string,
	placeholder: PropTypes.string,
	label: PropTypes.string,
	containerClassName: PropTypes.string,
	error: PropTypes.shape(),
	defaultHideCharacterLimit: PropTypes.bool,
	characterLimit: PropTypes.number,
	value: PropTypes.string,
	onChange: PropTypes.func
};
TextInput.defaultProps = {
	icon: null,
	type: 'text',
	size: 'medium',
	placeholder: '',
	label: '',
	containerClassName: null,
	error: null,
	id: null,
	defaultHideCharacterLimit: true,
	characterLimit: 0,
	value: null,
	onChange: null
};
